import React from "react";

import { Button, Box, CircularProgress, useTheme, withStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore, GetApp } from "@material-ui/icons";
import {
    DataGridPro,
    GridColDef,
    GridOverlay,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    useGridApiRef
} from "@mui/x-data-grid-pro";

import { useAppSelector } from "store";
import { Coordinates } from "modules/customer/tools/location/suggestedNewLocations/suggestedNewLocation";
import { selectSuggestedNewLocations } from "modules/customer/tools/location/suggestedNewLocations/suggestedNewLocationsSlice";

import Spacer from "components/Spacer";
import numberFormatter from "utils/numberFormatter";

import ExploreLocation from "./ExploreLocation";

const StyledDataGrid = withStyles(theme => ({
    root: {
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.dark,
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        '& .MuiCheckbox-root': {
            color: theme.palette.text.primary,
            '&.Mui-checked': {
                //@ts-ignore
                color: theme.palette.tertiary.main,
            },
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
            },
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none'
        }
    }
}))(DataGridPro);

const Table: React.FC = () => {
    const theme = useTheme();
    const suggestedNewLocations = useAppSelector(selectSuggestedNewLocations);
    const columns: GridColDef[] = [
        {
            field: 'address',
            type: 'string',
            flex: 1,
            headerName: 'Address',
            sortable: true,
            filterable: true
        },
        {
            field: 'coordinates',
            headerName: 'Explore location',
            flex: 1,
            sortable: false,
            filterable: false,
            disableExport: true,
            renderCell: (params) => {
                const coordinates: Coordinates = {
                    // @ts-ignore
                    latitude: params.value.latitude,
                    // @ts-ignore
                    longitude: params.value.longitude
                };
                return <ExploreLocation coordinates={coordinates} />;
            }
        },
        {
            field: 'overallScore',
            type: 'number',
            flex: 1,
            align: 'left',
            headerName: 'Overall alignment',
            headerAlign: 'left',
            sortable: true,
            filterable: true,
            renderCell: (params) => numberFormatter.toDecimalPlaces(params.value, 2) + " / 10"
        }
    ];

    const columnsWithWidth = columns.map(column => {
        if (column.type === 'singleSelect') {
            //@ts-ignore
            column.valueOptions = Array.from(new Set(suggestedNewLocations.data.map(item => item[column.field])));
        }
        return {
            ...column,
            minWidth: 200
        };
    });

    const apiRef = useGridApiRef();

    const handleExport = () => {
        apiRef.current.exportDataAsCsv({ fileName: "Dash suggested new locations" });
    };

    const CustomExportButton = () => {
        return (
            <Button
                // @ts-ignore
                style={{ color: theme.palette.tertiary.main }}
                startIcon={<GetApp />}
                onClick={handleExport}
                size="small"
            >
                Export
            </Button>
        );
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer style={{ paddingRight: theme.spacing(2) }}>
                <GridToolbarColumnsButton style={{ color: theme.palette.text.primary }} />
                <GridToolbarFilterButton style={{ color: theme.palette.text.primary }} />
                <Spacer />
                <CustomExportButton />
            </GridToolbarContainer>
        );
    }

    function CustomLoadingOverlay() {
        return (
            <GridOverlay
                style={{
                    // @ts-ignore
                    backgroundColor: theme.palette.quaternary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 1,
                }}
            >
                <CircularProgress />
            </GridOverlay>
        );
    }

    return (
        <Box height="100%" width="100%">
            <StyledDataGrid
                rows={suggestedNewLocations.data}
                columns={columnsWithWidth}
                checkboxSelection
                disableSelectionOnClick
                loading={suggestedNewLocations.isLoading}
                components={{
                    Toolbar: CustomToolbar,
                    ColumnSortedAscendingIcon: ExpandLess,
                    ColumnSortedDescendingIcon: ExpandMore,
                    LoadingOverlay: CustomLoadingOverlay,
                }}
                sortingOrder={['asc', 'desc']}
                apiRef={apiRef}
            />
        </Box>
    );
};

export default Table;
